import { useEffect, useRef } from 'react';
import { Chart, registerables, type ChartDataset } from 'chart.js';

Chart.register(...registerables);

const INK_MUTED = '#6b7280';
const GRID = '#f1f5f9';
const plainNumber = (n: number) => n.toLocaleString();

export interface WorkSeries {
  label: string;
  data: number[];
  colour: string;
  kind?: 'line' | 'bar';
  dashed?: boolean;
  /** Shade where this line is above the one before it (a shortfall). */
  fillToPrevious?: string;
}

/**
 * A line or bar chart over shared labels, with a crosshair tooltip that lists
 * every series at the hovered point. The legend is HTML, above the canvas,
 * so a series is named in words and never by colour alone.
 */
export default function WorkChart({
  labels, series, ariaLabel, height = 'h-56', stacked = false, valueText = plainNumber,
}: {
  labels: string[];
  series: WorkSeries[];
  ariaLabel: string;
  height?: string;
  stacked?: boolean;
  valueText?: (n: number) => string;
}) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const chartRef = useRef<Chart | null>(null);

  useEffect(() => {
    chartRef.current?.destroy();
    chartRef.current = null;
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    const datasets = series.map((s, i): ChartDataset<'bar' | 'line', number[]> => {
      if (s.kind === 'bar') {
        return {
          type: 'bar', label: s.label, data: s.data, backgroundColor: s.colour,
          borderRadius: 4, borderSkipped: 'bottom', borderColor: '#ffffff', borderWidth: stacked ? { top: 2 } : 0,
          maxBarThickness: 28, categoryPercentage: 0.8, barPercentage: 0.9,
        } as ChartDataset<'bar', number[]>;
      }
      return {
        type: 'line', label: s.label, data: s.data, borderColor: s.colour, backgroundColor: s.colour,
        borderWidth: 2, borderDash: s.dashed ? [5, 4] : undefined,
        pointRadius: labels.length > 40 ? 0 : 3, pointHoverRadius: 5, pointHitRadius: 12,
        pointBackgroundColor: s.colour, pointBorderColor: '#ffffff', pointBorderWidth: 1,
        tension: 0.25,
        // Shade only where this line is above the one before it (behind, not ahead).
        fill: s.fillToPrevious && i > 0 ? { target: '-1', above: s.fillToPrevious, below: 'transparent' } : false,
      } as ChartDataset<'line', number[]>;
    });
    chartRef.current = new Chart(ctx, {
      type: 'bar',
      data: { labels, datasets: datasets as ChartDataset<'bar', number[]>[] },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        interaction: { mode: 'index', intersect: false },
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: { label: (item) => `${item.dataset.label}: ${valueText(Number(item.raw) || 0)}` },
          },
        },
        scales: {
          x: {
            stacked,
            grid: { display: false },
            border: { color: GRID },
            ticks: { color: INK_MUTED, font: { size: 10 }, maxRotation: 0, autoSkip: true, maxTicksLimit: 8 },
          },
          y: {
            stacked,
            beginAtZero: true,
            grid: { color: GRID },
            border: { display: false },
            ticks: { color: INK_MUTED, font: { size: 10 }, maxTicksLimit: 5, precision: 0, callback: (v) => valueText(Number(v)) },
          },
        },
      },
    });
    return () => { chartRef.current?.destroy(); chartRef.current = null; };
  }, [labels, series, stacked, valueText]);

  return (
    <div className="space-y-2">
      {series.length > 1 && (
        <ul className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-neutral-600" aria-hidden="true">
          {series.map((s) => (
            <li key={s.label} className="flex items-center gap-1.5 min-w-0">
              <span
                className="inline-block h-2.5 w-2.5 shrink-0 rounded-sm"
                style={s.kind === 'bar' ? { background: s.colour } : { borderTop: `2px ${s.dashed ? 'dashed' : 'solid'} ${s.colour}`, height: 0, width: 12 }}
              />
              <span className="break-words">{s.label}</span>
            </li>
          ))}
        </ul>
      )}
      <div className={`relative w-full ${height}`} role="img" aria-label={ariaLabel}>
        <canvas ref={canvasRef} />
      </div>
    </div>
  );
}
